import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ScrollPill } from '@/components/atoms/ScrollPill/ScrollPill';
import { Skeleton } from '@/components/ui/skeleton';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import useMessages from '@/hooks/useMessages';
import { cn } from '@/lib/utils';
import MessageAreaPrologue from '@/modules/humanChat/components/MessageAreaPrologue/MessageAreaPrologue';
import { useComments } from '@/modules/humanChat/hooks/useComments';
import { useConversationEvents } from '@/modules/humanChat/hooks/useConversationEvents';
import { useConversation } from '@/modules/humanChat/hooks/useConversations';
import useRtmComments from '@/modules/humanChat/hooks/useRtmComments';
import useRtmConversationEvents from '@/modules/humanChat/hooks/useRtmConversationEvents';
import { selectSessionId } from '@/redux/session/selectors';
import { selectUserId } from '@/redux/user/selectors';
import {
  AuthorType,
  ConversationEvent,
  type ConversationEvent as ConversationEventType,
  ConversationSource,
  Message,
} from '@/shared/types/chat';
import type { Comment } from '@/shared/types/comments';
import { DEFAULT_START_MESSAGE } from '@/util/constants';

import { BrainStream } from './BrainStream/BrainStream';
import { Events, RENDERED_EVENTS } from './ConversationActivity/Events';
import { MessageGroup } from './ConversationActivity/MessageGroup';
import { NoteEvent } from './ConversationActivity/NoteEvent';
import { Reminder } from './ConversationActivity/Reminder';
import { mergeAndSortData, shouldShowAvatar, shouldShowTime } from './helpers';
import { TimeElement } from './TimeElement/TimeElement';
import { TriggerLabel } from './TriggerLabel/TriggerLabel';

interface Props {
  isLoading: boolean;
  onEditNote: (comment: Comment) => void;
  onCancelNoteEdit: () => void;
}

export type TimeElementType = { time_element: boolean; created: string };

export type MessageOrEvent = Message | ConversationEvent | TimeElementType | Comment;

// Treat the view as "at the bottom" within this distance (px) so minor offsets
// from variable-height content still keep the thread pinned to the latest message.
const SCROLL_BOTTOM_THRESHOLD = 100;

const MessageArea = ({ isLoading, onEditNote, onCancelNoteEdit }: Props) => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const sessionId = useSelector(selectSessionId);
  const userId = useSelector(selectUserId);
  const {
    conversation,
    reopenConversation,
    closeConversation,
    isExpired,
    isResolved,
    isSpam,
    isTrash,
    isExpiredAndResolved,
    isOpen,
  } = useConversation({ deskId, sessionId });

  const loadMoreRef = useRef(null);
  const oldScrollRef = useRef<number>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Whether the user is at (or near) the bottom of the thread. When true we keep
  // the view pinned to the latest message as content grows.
  const isAtBottomRef = useRef(true);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);

  const { messages, fetchNextPage, hasNextPage } = useMessages(deskId, sessionId);

  const { events } = useConversationEvents(deskId, sessionId);
  useRtmConversationEvents(deskId, sessionId);

  const { integrations } = useIntegrations(deskId);
  const channelByIntegration = new Map(
    (integrations ?? []).map((integration) => [integration.integration_id, integration.type])
  );

  const { comments, deleteComment } = useComments(deskId, sessionId);
  useRtmComments(deskId, sessionId);

  const handleDeleteNote = useCallback(
    (commentId: string) => {
      onCancelNoteEdit();
      deleteComment(commentId);
    },
    [onCancelNoteEdit, deleteComment]
  );

  const messagesPages = useMemo(() => [...(messages?.pages ?? [])], [messages?.pages]);
  const flatMessages = useMemo(
    () => messagesPages.flatMap((page) => page.messages),
    [messagesPages]
  );

  const { hasAutostartTrigger, firstMessageTimestamp } = useMemo(() => {
    const firstMessage = flatMessages[flatMessages.length - 1];
    return {
      hasAutostartTrigger: firstMessage?.text === DEFAULT_START_MESSAGE,
      firstMessageTimestamp: firstMessage?.created,
    };
  }, [flatMessages]);

  // https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates
  const isMessage = (item: MessageOrEvent): item is Message => 'message_id' in item;

  const isTimeElement = (item: MessageOrEvent): item is TimeElementType => 'time_element' in item;

  const isComment = (item: MessageOrEvent): item is Comment => 'comment_id' in item;

  // for saving some iterations in flatMessages
  const messageIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    flatMessages.forEach((message, index) => {
      map.set(message.message_id, index);
    });
    return map;
  }, [flatMessages]);

  const newestTimestamp = useMemo(() => {
    const newestMessage = flatMessages[0]?.created;
    const newestEvent = events[0]?.created;
    const newestComment = comments[comments.length - 1]?.created;

    const timestamps = [newestMessage, newestEvent, newestComment].filter(Boolean);

    if (timestamps.length === 0) {
      return null;
    }

    return timestamps.reduce((newest, current) =>
      new Date(current).getTime() > new Date(newest).getTime() ? current : newest
    );
  }, [comments, events, flatMessages]);

  // Only visitor messages should trigger the "Unread messages" button.
  const newestVisitorTimestamp = useMemo(
    () => flatMessages.find((m) => m.author_type === AuthorType.VISITOR)?.created ?? null,
    [flatMessages]
  );

  // The operator's own sent message — used to snap the view to the bottom even
  // when they were scrolled up, so they always see what they just sent.
  const newestOwnTimestamp = useMemo(
    () => flatMessages.find((m) => m.author_id === userId)?.created ?? null,
    [flatMessages, userId]
  );

  const scrollToBottom = useCallback(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, []);

  const handleScrollToBottom = useCallback(() => {
    scrollToBottom();
    isAtBottomRef.current = true;
    setIsScrolledUp(false);
    setHasUnread(false);
  }, [scrollToBottom]);

  // Only used as the onScroll handler, so it doesn't need to be memoized.
  const handleScroll = () => {
    const el = bodyRef.current;
    if (!el) {
      return;
    }
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_BOTTOM_THRESHOLD;
    isAtBottomRef.current = atBottom;
    setIsScrolledUp(!atBottom);
    if (atBottom) {
      setHasUnread(false);
    }
  };

  // Switching conversations reuses this component (the route only swaps the
  // sessionId), so reset the scroll state — otherwise a "scrolled up" flag from
  // the previous conversation leaks in and the new one opens mid-thread with a
  // stale unread button.
  useLayoutEffect(() => {
    isAtBottomRef.current = true;
    setIsScrolledUp(false);
    setHasUnread(false);
  }, [sessionId]);

  // Any new content arrived and the view is at the bottom — keep it pinned there.
  // useLayoutEffect runs before paint so there's no visible jump.
  useLayoutEffect(() => {
    if (!isLoading && newestTimestamp && isAtBottomRef.current) {
      scrollToBottom();
    }
  }, [isLoading, newestTimestamp, scrollToBottom]);

  // A visitor message arrived while the operator is scrolled up — surface the
  // button so they can jump down without losing their reading position.
  useLayoutEffect(() => {
    if (!isLoading && newestVisitorTimestamp && !isAtBottomRef.current) {
      setHasUnread(true);
    }
  }, [isLoading, newestVisitorTimestamp]);

  // The operator sent a message — always jump to the bottom so they see it,
  // even if they had scrolled up (and clear any pending unread button).
  useLayoutEffect(() => {
    if (!isLoading && newestOwnTimestamp) {
      handleScrollToBottom();
    }
  }, [isLoading, newestOwnTimestamp, handleScrollToBottom]);

  // Catch async height growth that doesn't trigger a React render (attachment
  // images/media finishing layout after the initial paint).
  useEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return;
    }
    const observer = new ResizeObserver(() => {
      if (isAtBottomRef.current) {
        scrollToBottom();
      }
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [scrollToBottom]);

  // Handle scroll position after loading more messages (pre-paint to avoid a
  // flash). The bottom-pin effect above is a no-op here since loading older
  // messages sets isAtBottomRef to false.
  useLayoutEffect(() => {
    if (bodyRef.current && messages?.pages.length > 1) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight - oldScrollRef.current;
    }
  }, [messages?.pages.length]);

  const handleNextPageFetch = useCallback(() => {
    // The user is scrolling up to load older messages — stop pinning to bottom.
    isAtBottomRef.current = false;
    oldScrollRef.current = bodyRef.current.scrollHeight;
    return fetchNextPage();
  }, [fetchNextPage]);

  useIntersectionObserver({ target: loadMoreRef, onIntersect: handleNextPageFetch });

  const mergedData = useMemo(
    () => mergeAndSortData(flatMessages, [...events, ...comments]),
    [events, flatMessages, comments]
  );

  const firstTimeElementIndex = useMemo(() => mergedData.findIndex(isTimeElement), [mergedData]);

  type Entry = { item: MessageOrEvent; index: number };

  type Group = { items: Entry[]; tight: boolean };

  type Segment =
    { kind: 'time'; item: MessageOrEvent; index: number } | { kind: 'messages'; items: Entry[] };

  const messageSegments = useMemo((): Segment[] => {
    const result: Segment[] = [];
    let batch: Entry[] = [];

    mergedData.forEach((item, index) => {
      if (isTimeElement(item)) {
        if (batch.length > 0) {
          result.push({ kind: 'messages', items: batch });
          batch = [];
        }
        result.push({ kind: 'time', item, index });
      } else {
        batch.push({ item, index });
      }
    });
    if (batch.length > 0) {
      result.push({ kind: 'messages', items: batch });
    }
    return result;
  }, [mergedData]);

  const source: ConversationSource = useMemo(
    () => ({
      deskId: conversation?.desk_id,
      integrationId: conversation?.integration_id,
      channel: conversation?.channel,
      sessionId: conversation?.session_id,
    }),
    [
      conversation?.channel,
      conversation?.desk_id,
      conversation?.integration_id,
      conversation?.session_id,
    ]
  );

  const renderMessage = (message, index) => {
    const showTime = shouldShowTime({
      currentMessage: message,
      nextMessage: flatMessages[index - 1],
      index,
    });

    const showAvatar = shouldShowAvatar({
      currentMessage: message,
      nextMessage: flatMessages[index - 1],
      prevMessage: flatMessages[index + 1],
      index,
    });

    return (
      <MessageGroup
        message={message}
        key={message.message_id}
        showTime={showTime}
        showAvatar={showAvatar}
        source={source}
        channel={channelByIntegration.get(message.integration_id)}
      />
    );
  };

  const rendersNothing = (item: MessageOrEvent) => {
    if (isMessage(item)) {
      return item.text === DEFAULT_START_MESSAGE;
    }
    if (isComment(item)) {
      return false;
    }
    return !RENDERED_EVENTS.has((item as ConversationEventType).type);
  };

  const closesGroup = (message: Message) => {
    const index = messageIndexMap.get(message.message_id);
    return shouldShowTime({ currentMessage: message, nextMessage: flatMessages[index - 1], index });
  };

  const isReminder = (item: MessageOrEvent) =>
    isMessage(item) && Boolean(item.body && 'reminder_id' in item.body);

  const isEventRow = (item: MessageOrEvent) =>
    isReminder(item) ||
    (!isMessage(item) &&
      !isComment(item) &&
      (item as ConversationEventType).type !== ConversationEvent.SUMMARY);

  const toGroups = (items: Entry[]) => {
    const groups: Group[] = [];
    let group: Entry[] = [];
    let tight = false;

    const close = () => {
      if (group.length > 0) {
        groups.push({ items: group, tight });
        group = [];
      }
    };

    items.forEach((entry) => {
      if (rendersNothing(entry.item)) {
        return;
      }
      if (isEventRow(entry.item)) {
        if (!tight) {
          close();
          tight = true;
        }
        group.push(entry);
        return;
      }
      if (tight) {
        close();
        tight = false;
      }
      if (!isMessage(entry.item)) {
        close();
        groups.push({ items: [entry], tight: false });
        return;
      }
      group.push(entry);
      if (closesGroup(entry.item)) {
        close();
      }
    });
    close();

    return groups;
  };

  const groupKey = ({ item, index }: Entry) => {
    if (isMessage(item)) {
      return item.message_id;
    }
    if (isComment(item)) {
      return item.comment_id;
    }
    return (item as ConversationEvent).event_id ?? index;
  };

  const renderMessageOrEvent = (item: MessageOrEvent, index: number) => {
    if (isMessage(item)) {
      if (item.text === DEFAULT_START_MESSAGE) {
        return null;
      }

      return item.body && 'reminder_id' in item.body ? (
        <Reminder key={item.message_id} minutes={item.body.minutes} />
      ) : (
        renderMessage(item, messageIndexMap.get(item.message_id))
      );
    }

    if (isTimeElement(item)) {
      return (
        <Fragment key={item.created}>
          <TimeElement created={item.created} />
          {hasAutostartTrigger && index === firstTimeElementIndex && (
            <TriggerLabel created={firstMessageTimestamp} />
          )}
        </Fragment>
      );
    }

    if (isComment(item)) {
      return (
        <NoteEvent
          key={item.comment_id}
          comment={item}
          onEdit={onEditNote}
          onDelete={handleDeleteNote}
        />
      );
    }

    return (
      <Events
        key={(item as ConversationEvent).event_id}
        event={item}
        visitorName={conversation?.context?.user?.display_name}
      />
    );
  };

  return (
    <>
      <MessageAreaPrologue
        conversation={conversation}
        reopenConversation={reopenConversation}
        closeConversation={closeConversation}
        isExpired={isExpired}
        isResolved={isResolved}
        isSpam={isSpam}
        isTrash={isTrash}
        isExpiredAndResolved={isExpiredAndResolved}
        isOpen={isOpen}
      />

      <div className="relative min-h-0 flex-1">
        <div
          className="flex h-full flex-col overflow-x-hidden overflow-y-auto px-4"
          ref={bodyRef}
          onScroll={handleScroll}
        >
          <div
            className="flex shrink-0 grow basis-0 flex-col items-start gap-6 self-stretch py-6 wrap-anywhere"
            ref={contentRef}
          >
            {hasNextPage !== false && (
              <div className="mx-auto w-full max-w-3xl">
                <div ref={loadMoreRef}>
                  <Skeleton className="h-10 w-45" />
                </div>
              </div>
            )}
            {messageSegments.map((seg) =>
              seg.kind === 'time' ? (
                renderMessageOrEvent(seg.item, seg.index)
              ) : (
                <Fragment key={groupKey(seg.items[0])}>
                  {toGroups(seg.items).map(({ items, tight }) => (
                    <div
                      key={groupKey(items[0])}
                      className={cn(
                        'mx-auto flex w-full max-w-3xl flex-col',
                        tight ? 'gap-0' : 'gap-1'
                      )}
                    >
                      {items.map(({ item, index }) => renderMessageOrEvent(item, index))}
                    </div>
                  ))}
                </Fragment>
              )
            )}
            <div className="mx-auto w-full max-w-3xl">
              <BrainStream sessionId={sessionId} />
            </div>
          </div>
        </div>
        {isScrolledUp && (
          <ScrollPill direction="down" onClick={handleScrollToBottom}>
            {hasUnread ? t('conversation.view_unread') : t('conversation.view_latest')}
          </ScrollPill>
        )}
      </div>
    </>
  );
};

export default memo(MessageArea);
